/*
 * VerqoShippingClaimTimeline — renders the full history of an EasyPost-
 * backed parcel claim (damage/loss/theft) inline in the buyer/seller
 * claim detail drawer. Auto-fetches live status when opened.
 *
 * Props:
 *   claimId   — Verqo dropship_claim id (uuid string)
 *   role      — "buyer" | "seller" | "admin" — controls whether Cancel
 *               is available (buyer + admin only per backend guard).
 *   onUpdate  — optional callback fired after refresh/cancel; parent
 *               can use it to re-render list rows.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Shield, RefreshCcw, XCircle, CheckCircle2, Clock, AlertTriangle, Truck } from "lucide-react";

import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";


const STATUS_TONE = {
  submitted:        { tone: "bg-blue-50 text-blue-700 border-blue-200",         icon: Clock },
  in_review:        { tone: "bg-amber-50 text-amber-700 border-amber-200",      icon: Clock },
  approved:         { tone: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  approved_partial: { tone: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  rejected:         { tone: "bg-rose-50 text-rose-700 border-rose-200",         icon: XCircle },
  cancelled:        { tone: "bg-neutral-100 text-neutral-600 border-neutral-200", icon: XCircle },
  needs_action:     { tone: "bg-orange-50 text-orange-700 border-orange-200",   icon: AlertTriangle },
};


export default function VerqoShippingClaimTimeline({ claimId, role = "buyer", onUpdate }) {
  const [loading, setLoading]  = useState(false);
  const [data, setData]        = useState(null);      // { ep_claim, easypost_claim_id, ... }
  const [cancelling, setCancel] = useState(false);
  const [error, setError]      = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get(`/dropship/claims/${claimId}/verqo-shipping-claim`);
      setData(data);
      onUpdate?.(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load Verqo Shipping claim");
    } finally {
      setLoading(false);
    }
  }, [claimId, onUpdate]);

  useEffect(() => { refresh(); }, [refresh]);

  const cancel = async () => {
    if (!window.confirm("Cancel this Verqo Shipping claim? This can&apos;t be undone.")) return;
    setCancel(true);
    try {
      const { data } = await api.post(`/dropship/claims/${claimId}/verqo-shipping-claim/cancel`);
      toast.success("Claim cancelled with Verqo Shipping");
      setData(prev => ({ ...prev, ep_claim: data.ep_claim }));
      onUpdate?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel");
    } finally {
      setCancel(false);
    }
  };

  // No EP claim on this dropship claim — quiet skeleton.
  if (!data?.easypost_claim_id && !loading) return null;

  const ep = data?.ep_claim || {};
  const status = ep.status || "submitted";
  const tone   = STATUS_TONE[status] || STATUS_TONE.submitted;
  const StatusIcon = tone.icon;
  const canCancel = (role === "buyer" || role === "admin")
                    && !["cancelled", "approved", "rejected"].includes(status);

  return (
    <div className="rounded-xl border border-border bg-white p-3" data-testid="verqo-shipping-claim-timeline">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/65 inline-flex items-center gap-1.5">
          <Shield className="w-3 h-3" />
          Verqo Shipping · Carrier Claim
        </div>
        <div className="flex items-center gap-1.5">
          {ep.id && (
            <span className="text-[10px] mono text-foreground/50" title={ep.id}>
              {ep.id.slice(0, 12)}…
            </span>
          )}
          <Button
            size="sm" variant="ghost"
            onClick={refresh} disabled={loading}
            data-testid="verqo-shipping-claim-refresh"
            className="h-6 px-1.5"
          >
            <RefreshCcw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {loading && !ep.id && (
        <div className="text-[11px] text-foreground/50 py-2">Loading Verqo Shipping status…</div>
      )}
      {error && (
        <div className="text-[11px] text-rose-700 py-1.5 bg-rose-50 border border-rose-200 rounded px-2">
          {error}
        </div>
      )}

      {ep.id && (
        <>
          {/* Status pill row */}
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <span className={`text-[10px] mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full border inline-flex items-center gap-1 ${tone.tone}`}
                  data-testid={`verqo-shipping-claim-status-${status}`}>
              <StatusIcon className="w-2.5 h-2.5"/>{status.replace("_", " ")}
            </span>
            <Badge variant="secondary" className="text-[10px]">{ep.type} claim</Badge>
            {ep.tracking_code && (
              <span className="text-[10px] mono text-foreground/60 inline-flex items-center gap-1">
                <Truck className="w-3 h-3"/>{ep.tracking_code}
              </span>
            )}
          </div>

          {/* Amount row */}
          <div className="grid grid-cols-3 gap-2 text-[11px] mb-3">
            <div>
              <div className="text-[9.5px] mono uppercase text-foreground/50">Requested</div>
              <div className="font-medium">${Number(ep.requested_amount || 0).toFixed(2)}</div>
            </div>
            <div>
              <div className="text-[9.5px] mono uppercase text-foreground/50">Insured</div>
              <div>${Number(ep.insurance_amount || 0).toFixed(2)}</div>
            </div>
            <div>
              <div className="text-[9.5px] mono uppercase text-foreground/50">Approved</div>
              <div className={ep.approved_amount ? "text-emerald-700 font-medium" : "text-foreground/40"}>
                {ep.approved_amount ? `$${Number(ep.approved_amount).toFixed(2)}` : "—"}
              </div>
            </div>
          </div>

          {/* Status detail message */}
          {ep.status_detail && (
            <div className="text-[11px] text-foreground/70 bg-neutral-50 border border-border rounded px-2 py-1.5 mb-3">
              {ep.status_detail}
            </div>
          )}

          {/* History timeline */}
          {(ep.history || []).length > 0 && (
            <div className="border-t border-border pt-2">
              <div className="text-[9.5px] mono uppercase text-foreground/50 mb-1.5">History</div>
              <ol className="space-y-1.5">
                {(ep.history || []).map((h, i) => {
                  const ht = STATUS_TONE[h.status] || STATUS_TONE.submitted;
                  const HIcon = ht.icon;
                  return (
                    <li key={i} className="flex items-start gap-2 text-[10.5px]">
                      <HIcon className="w-3 h-3 mt-0.5 flex-shrink-0 text-foreground/50"/>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className={`px-1 py-0 rounded text-[9.5px] mono uppercase border ${ht.tone}`}>
                            {h.status?.replace("_", " ")}
                          </span>
                          <span className="text-foreground/50 text-[9.5px]">
                            {h.status_timestamp ? new Date(h.status_timestamp).toLocaleString() : ""}
                          </span>
                        </div>
                        {h.status_detail && (
                          <div className="text-foreground/70 mt-0.5">{h.status_detail}</div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          )}

          {/* Cancel button (buyer + admin only, non-terminal statuses) */}
          {canCancel && (
            <div className="border-t border-border pt-2 mt-2">
              <Button
                size="sm" variant="ghost"
                onClick={cancel} disabled={cancelling}
                data-testid="verqo-shipping-claim-cancel"
                className="text-rose-600 hover:text-rose-700 h-7 text-[11px]"
              >
                <XCircle className="w-3 h-3 mr-1"/>
                {cancelling ? "Cancelling…" : "Cancel this Verqo Shipping claim"}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
